import type { DisplayCreative, IDimensionDefinition } from '@feathr/blackbox';
import {
  CreativeClass,
  metaAspectRatiosMatch,
  ValidDimensions,
  ValidFacebookDimensions,
} from '@feathr/blackbox';

/** Dimension keys shown in the "Most popular" section for display ads. */
export const displayMostPopularKeys = ['300x250', '728x90', '160x600', '300x600'];

/** Dimension keys shown in the "Most popular" section for Meta creatives. */
export const metaMostPopularKeys = ['1080x1080', '1080x1350', '1080x1920', '1920x1080'];

/** Display dimensions not in the "Most popular" list. */
export const displayRemainingDimensions: IDimensionDefinition[] = Object.entries(ValidDimensions)
  .filter(([key]) => !displayMostPopularKeys.includes(key))
  .map(([, dimension]) => dimension);

/** Meta dimension keys not in the "Most popular" list. */
export const metaRemainingKeys: string[] = Object.keys(ValidFacebookDimensions).filter(
  (key) => !metaMostPopularKeys.includes(key),
);

/** Whether the Meta section has dimensions beyond the most popular ones. */
export const hasMetaRemainingDimensions: boolean = metaRemainingKeys.length > 0;

/**
 * Checks if any enabled, non-archived creative exactly matches the given display dimensions.
 *
 * @param creatives - The list of creatives to check
 * @param width - Target width
 * @param height - Target height
 * @returns True if a matching creative exists
 */
export function isDisplayDimensionComplete(
  creatives: DisplayCreative[],
  width: number,
  height: number,
): boolean {
  return creatives.some(
    (c) =>
      !c.get('is_archived') &&
      c.get('is_enabled') &&
      c.get('width') === width &&
      c.get('height') === height,
  );
}

/**
 * Checks if any enabled, non-archived Meta creative matches the given dimensions
 * using aspect ratio tolerance. Filters by Facebook creative class and type (image vs video).
 *
 * @param creatives - The list of creatives to check
 * @param width - Target minimum width
 * @param height - Target minimum height
 * @param isVideo - Whether to check for video creatives (false = image)
 * @returns True if a matching creative exists
 */
export function isMetaDimensionComplete(
  creatives: DisplayCreative[],
  width: number,
  height: number,
  isVideo: boolean,
): boolean {
  const targetRatio = width / height;
  const videoCls = CreativeClass.FacebookVideo;
  const imageCls = CreativeClass.FacebookImage;

  return creatives.some((c) => {
    if (c.get('is_archived') || !c.get('is_enabled')) {
      return false;
    }
    const cls = c.get('_cls');
    if (isVideo ? cls !== videoCls : cls !== imageCls) {
      return false;
    }
    const cw = c.get('width');
    const ch = c.get('height');
    return metaAspectRatiosMatch(cw / ch, targetRatio) && cw >= width && ch >= height;
  });
}
